<template>
  <section>
    <h1>Super Admin</h1>
    <b-tabs align="center" justified pills card v-model="tab">
      <b-tab title="Orders" class="">
        <div class="d-flex flex-wrap align-items-end">
          <b-form-group label="Search" class="mr-2 flex-grow-1">
            <b-form-input v-model="filter" debounce="400" @update="reload"
                          placeholder="Name, mobile, #order or tracking"></b-form-input>
          </b-form-group>
          <b-form-group label="Order Status" class="mr-2">
            <b-form-select v-model="orderStatus" @change="reload">
              <option value="ALL">ALL</option>
              <option value="CART">CART (not ordered yet)</option>
              <option v-for="st of orderStatuses" :key="st" :value="st">{{ st }}</option>
            </b-form-select>
          </b-form-group>
          <b-form-group label="Payment Status" class="mr-2">
            <b-form-select v-model="paymentStatus" @change="reload" :disabled="isCartView">
              <option value="ALL">ALL</option>
              <option v-for="st of paymentStatuses" :key="st" :value="st">{{ st }}</option>
            </b-form-select>
          </b-form-group>
          <b-form-group label="Brand" class="mr-2">
            <b-form-select v-model="brandId" @change="reload">
              <option value="ALL">ALL</option>
              <option v-for="b of $store.state.brands" :key="b.id" :value="b.id">{{ b.name }}</option>
            </b-form-select>
          </b-form-group>
          <b-form-group label="Last N days" class="mr-2">
            <b-form-spinbutton v-model="max" min="1" max="365" @change="reload"></b-form-spinbutton>
          </b-form-group>
          <b-form-group class="mr-2">
            <b-button variant="info" @click="refresh" :disabled="loadingOrders || loadingCarts">
              <font-awesome-icon :icon="['far','sync']" :spin="loadingOrders || loadingCarts"></font-awesome-icon>
            </b-button>
          </b-form-group>
        </div>

        <template v-if="isCartView">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <small class="text-muted">
              {{ filteredCarts.length }} open cart(s) &middot; <rupee>{{ cartsTotal }}</rupee>
            </small>
          </div>
          <div class="text-center my-3" v-if="loadingCarts && !carts.length">
            <b-spinner small></b-spinner>
          </div>
          <div class="text-center text-muted my-3" v-if="!loadingCarts && !filteredCarts.length">No open carts</div>

          <div class="list-group list-group-flush">
            <div class="list-group-item" v-for="c of filteredCarts" :key="c.session">
              <div class="row">
                <div class="col-12 col-md-4">
                  <div class="d-flex flex-column">
                    <div>
                      <b-badge variant="warning">CART</b-badge> &ndash;
                      <font-awesome-icon :icon="['far','clock']"></font-awesome-icon>
                      <span :title="c.updatedAt || c.createdAt">{{ ago(c.updatedAt || c.createdAt) }}</span>
                      <b-spinner small v-if="saving[c.session]" class="ml-2"></b-spinner>
                    </div>
                    <div class="mt-1">
                      <font-awesome-icon :icon="['far', 'user']"></font-awesome-icon>
                      {{ c.customerName }} <small class="text-muted">{{ c.customerMobile }}</small>
                    </div>
                    <div class="mt-1">
                      <rupee>{{ c.total }}</rupee>
                      <small class="text-muted">({{ c.items.length }} item(s))</small>
                    </div>
                    <div class="mt-1" v-if="c.brandId">
                      <display-brand :id="c.brandId" :border="false"></display-brand>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-md-8">
                  <table class="table table-sm table-borderless small mb-2">
                    <tbody>
                    <tr v-for="li of c.items" :key="li.variantId">
                      <td>{{ li.name }} <span class="text-muted">({{ li.variantName }})</span></td>
                      <td class="text-right text-nowrap">{{ li.qty }} &times; <rupee>{{ li.price }}</rupee></td>
                    </tr>
                    </tbody>
                  </table>
                  <div class="d-flex flex-column flex-md-row align-items-md-center">
                    <b-form-select size="sm" v-model="c.addressId" class="mr-md-2 mb-2 mb-md-0"
                                   :options="cartAddressOptions(c)" value-field="id" text-field="displayAddress"
                                   @focus.native="loadAddresses(c)" @mouseenter.native="loadAddresses(c)">
                      <template #first>
                        <b-form-select-option :value="null">-- address for the order --</b-form-select-option>
                      </template>
                    </b-form-select>
                    <b-button-group size="sm" class="text-nowrap">
                      <b-button variant="success" :disabled="!c.addressId || saving[c.session]"
                                @click="cartToOrder(c)">
                        Create Order
                      </b-button>
                      <b-button variant="outline-danger" :disabled="saving[c.session]" @click="deleteCart(c)">
                        Delete
                      </b-button>
                    </b-button-group>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <small class="text-muted">{{ total }} order(s)</small>
          <b-pagination v-if="total > pageSize" v-model="page" :total-rows="total" :per-page="pageSize"
                        size="sm" class="mb-0" @change="p => { page = p; load() }"></b-pagination>
        </div>

        <div class="text-center my-3" v-if="loadingOrders && !orders.length">
          <b-spinner small></b-spinner>
        </div>
        <div class="text-center text-muted my-3" v-if="!loadingOrders && !orders.length">No orders</div>

        <div class="list-group list-group-flush">
          <div class="list-group-item" v-for="o of orders" :key="o.id" :class="{'saving': saving[o.id]}">
            <div class="row">
              <div class="col-12 col-md-4">
                <div class="d-flex flex-column">
                  <div>
                    <strong>#{{ o.orderNum }}</strong> &ndash;
                    <font-awesome-icon icon="calendar"></font-awesome-icon>
                    {{ o.orderDate.substring(0, 10) }}
                    <b-spinner small v-if="saving[o.id]" class="ml-2"></b-spinner>
                  </div>
                  <div class="mt-1">
                    <font-awesome-icon :icon="['far', 'user']"></font-awesome-icon>
                    {{ o.person || o.customerName }} <small class="text-muted">{{ o.customerMobile }}</small>
                  </div>
                  <div class="mt-1">
                    <font-awesome-icon :icon="['far', 'building']"></font-awesome-icon>
                    {{ o.fullAddress }}
                  </div>
                  <div class="mt-1">
                    <rupee>{{ o.netValue }}</rupee>
                  </div>
                  <div class="mt-1" v-if="o.brandId">
                    <display-brand :id="o.brandId" :border="false"></display-brand>
                  </div>
                </div>
              </div>

              <div class="col-12 col-md-8">
                <div class="row row-cols-1 row-cols-md-3">
                  <b-form-group label="Order Status" class="col">
                    <b-form-select size="sm" v-model="o.orderStatus" :options="orderStatuses"
                                   @change="updateOrder(o)"></b-form-select>
                  </b-form-group>
                  <b-form-group label="Address" class="col">
                    <b-form-select size="sm" v-model="o.customerAddressId"
                                   :options="addressOptions(o)" value-field="id" text-field="displayAddress"
                                   @focus.native="loadAddresses(o)"
                                   @mouseenter.native="loadAddresses(o)"
                                   @change="updateOrder(o)"></b-form-select>
                  </b-form-group>
                  <b-form-group label="Ship Via" class="col">
                    <b-form-select size="sm" v-model="o.modeOfShipment" :options="shipModes"
                                   @change="updateOrder(o)"></b-form-select>
                  </b-form-group>
                  <b-form-group label="Shipment ID" class="col">
                    <b-form-input size="sm" v-model="o.remoteShipId" lazy @change="updateOrder(o)"/>
                  </b-form-group>
                  <b-form-group label="Tracking Number" class="col">
                    <b-form-input size="sm" v-model="o.trackingNumber" lazy @change="updateOrder(o)"/>
                  </b-form-group>
                  <b-form-group label="Tracking Url" class="col">
                    <b-form-input size="sm" v-model="o.trackingUrl" lazy @change="updateOrder(o)"/>
                  </b-form-group>
                  <b-form-group label="Payment Status" class="col">
                    <b-form-select size="sm" v-model="o.paymentStatus" :options="paymentStatuses"
                                   @change="updateOrder(o)"></b-form-select>
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-center mt-2" v-if="total > pageSize">
          <b-pagination v-model="page" :total-rows="total" :per-page="pageSize" size="sm"
                        @change="p => { page = p; load() }"></b-pagination>
        </div>
        </template>
      </b-tab>
      <b-tab title="Welcome Message" class="">

        <b-tabs content-class="mt-3" class="mt-3" pills card>
          <b-tab v-for="(welcomeMessage, idx) of welcomeMessages"
                 :key="welcomeMessage.id"
                 :active="idx === 0">
            <template v-slot:title>
              <display-brand :id="welcomeMessage.brand.id" :border="false"></display-brand>
            </template>
            <b-form>
              <b-form-group label="Message">
                <b-form-textarea v-model="welcomeMessage.message" class="mb-3"></b-form-textarea>
              </b-form-group>
              <b-form-group label="">
                <b-form-checkbox
                    :id="'checkbox-'+welcomeMessage.brand.id"
                    v-model="welcomeMessage.sendCatalog"
                    :name="'checkbox-'+welcomeMessage.brand.id"
                    :value="true"
                    :unchecked-value="false"
                >
                  Send Catalog
                </b-form-checkbox>
              </b-form-group>
              <b-form-group label="Attachments">
                <b-form-file v-model="welcomeMessage.attachmentsNew" multiple class="mb-3"></b-form-file>
              </b-form-group>
              <b-form-group label="Uploaded Files">
                <div class="d-flex flex-wrap">
                  <a href="#"
                     v-for="a of welcomeMessage.attachments"
                     @click.prevent="showImg(a)"
                     :key="a">
                    <b-img-lazy
                        v-bind="mainProps"
                        :src="makeUrl(a)"
                        fluid thumbnail></b-img-lazy>
                  </a>
                </div>
              </b-form-group>
              <div class="d-flex justify-content-center justify-content-lg-end">
                <b-button @click="saveWM(welcomeMessage)">
                  <font-awesome-icon icon="save"></font-awesome-icon>
                  Save for '{{ welcomeMessage.brand.name }}'
                </b-button>
              </div>
            </b-form>
          </b-tab>
        </b-tabs>


        <b-modal id="img" hide-footer hide-header>
          <b-img-lazy
              :src="showingImg"
              fluid></b-img-lazy>
        </b-modal>
      </b-tab>
      <b-tab title="DND" class="">


        <h2 class="mb-3 mt-3">DND Number</h2>

        <b-form-input v-model="dnd" class="mb-3"></b-form-input>


        <b-button @click="saveDND()">
          <font-awesome-icon icon="save"></font-awesome-icon>
          Save DND
        </b-button>

        <h2 class="mt-3">Existing</h2>
        <table class="table">
          <thead>
          <tr>
            <th>Mobile</th>
            <th>Name</th>
            <th>Added On</th>
            <th>&nbsp;</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="d of dnds" :key="d.id">
            <td>{{ d.mobile }}</td>
            <td>{{ d.name }}</td>
            <td>{{ d.modifiedAt }}</td>
            <td>
              <b-button @click="removeFromDND(d)">
                <font-awesome-icon icon="times"></font-awesome-icon>
              </b-button>
            </td>
          </tr>
          </tbody>
        </table>
      </b-tab>
      <b-tab title="Config" class="">

        <div
            class="d-flex flex-column flex-md-row justify-content-between mb-3 align-items-center align-content-center mt-3">
          <h3>Config</h3>
          <div class="d-flex flex-column flex-md-row justify-content-center align-items-center align-content-center">

            <div
                class="mr-2 d-flex flex-column flex-md-row justify-content-center align-content-center align-items-center">
              <b-form-checkbox v-model="modeCfg" name="check-button" switch class="mb-3 mb-md-0">
                <span>Card?</span>
              </b-form-checkbox>
              <input type="text" v-model="filterCfg" class="form-control form-control-sm ml-md-2 mb-3 mb-md-0"
                     placeholder="Search">
              <b-form-select size="sm" class="ml-md-2 mb-3 mb-md-0" :options="$store.state.brands" value-field="id"
                             text-field="name"
                             v-model="filterBrand"/>
            </div>

            <b-button-group class="mt-3 mt-md-0" size="sm">
              <b-button @click="addCFG" variant="success">
                <font-awesome-icon icon="plus"></font-awesome-icon>
                New CFG
              </b-button>
              <b-button @click="loadCFG" variant="primary">
                <font-awesome-icon icon="sync"></font-awesome-icon>
                Refresh
              </b-button>
              <b-button @click="resetCFG" variant="warning">
                <font-awesome-icon icon="times-circle"></font-awesome-icon>
                Clear
              </b-button>
              <a :href="googleUrl" class="btn btn-info" target="_blank">
                <font-awesome-icon :icon="['fab', 'google']"></font-awesome-icon>
                Connect Google
              </a>
            </b-button-group>
          </div>
        </div>
        <div class="d-flex flex-column flex-md-row flex-wrap" v-if="modeCfg">

          <div class="card mb-3 mr-md-3" v-for="(cfg, idx) of filterConfigs" :key="idx" style="min-width: 20em">
            <div class="list-group list-group-flush">
              <div class="list-group-item d-flex">
                <display-brand v-if="isSaved(cfg) && cfg.theKey.brandId !== null"
                               :width="24"
                               :id="cfg.theKey.brandId" :border="false"></display-brand>
                <b-form-group label="Brand" v-else>
                  <b-form-select
                      size="sm"
                      :options="$store.state.brands" v-model="cfg.theKey.brandId" value-field="id" text-field="name"
                      :disabled="isSaved(cfg) && cfg.theKey.brandId !== null"
                  ></b-form-select>
                </b-form-group>
              </div>
              <div class="list-group-item">
                <b-form-group label="Key">
                  <input type="hidden" :value="cfg.theKey.theKey"/>
                  <b-form-input size="sm" v-model="cfg.theKey.theKey" :disabled="isSaved(cfg)"></b-form-input>
                </b-form-group>
              </div>
              <div class="list-group-item">
                <b-form-group label="Value Type">
                  <b-form-select
                      size="sm"
                      :options="$store.state.valueTypes" v-model="cfg.valueType"
                      :disabled="isSaved(cfg) && cfg.valueType"
                  ></b-form-select>
                </b-form-group>
              </div>
              <div class="list-group-item">
                <b-form-group label="Value">
                  <b-form-textarea size="sm" v-model="cfg.theValue"></b-form-textarea>
                </b-form-group>
              </div>

              <div class="list-group-item d-flex justify-content-end">
                <b-button-group size="sm">
                  <b-button @click="saveCFG(cfg)">
                    <font-awesome-icon icon="save"></font-awesome-icon>
                    Save
                  </b-button>
                  <b-button @click="deleteCFG(cfg)" variant="danger">
                    <font-awesome-icon icon="times"></font-awesome-icon>
                    Delete
                  </b-button>
                </b-button-group>

              </div>
            </div>
          </div>
        </div>
        <table class="table table-borderless" v-else>
          <thead class="thead-dark">
          <tr>
            <th>Key</th>
            <th>Type</th>
            <th>Value</th>
            <th>Brand</th>
            <th>&nbsp;</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(cfg, idx) of filterConfigs" :key="idx">
            <td>
              <input type="hidden" :value="cfg.theKey.theKey"/>
              <b-form-input size="sm" v-model="cfg.theKey.theKey" :disabled="isSaved(cfg)"></b-form-input>
            </td>
            <td>
              <b-form-select
                  size="sm"
                  :options="$store.state.valueTypes" v-model="cfg.valueType"
                  :disabled="isSaved(cfg) && cfg.valueType"
              ></b-form-select>
            </td>
            <td>
              <b-form-textarea size="sm" v-model="cfg.theValue"></b-form-textarea>
            </td>
            <td>
              <display-brand v-if="isSaved(cfg) && cfg.theKey.brandId !== null"
                             :name="false"
                             :id="cfg.theKey.brandId" :border="false"></display-brand>
              <b-form-group label="Brand" v-else>
                <b-form-select
                    size="sm"
                    :options="$store.state.brands" v-model="cfg.theKey.brandId" value-field="id" text-field="name"
                    :disabled="isSaved(cfg) && cfg.theKey.brandId !== null"
                ></b-form-select>
              </b-form-group>
            </td>
            <td>
              <b-button-group size="sm">
                <b-button @click="saveCFG(cfg)">
                  <font-awesome-icon icon="save"></font-awesome-icon>
                  Save
                </b-button>
                <b-button @click="deleteCFG(cfg)" variant="danger">
                  <font-awesome-icon icon="times"></font-awesome-icon>
                  Delete
                </b-button>
              </b-button-group>
            </td>
          </tr>
          </tbody>
        </table>
      </b-tab>
      <b-tab title="Review" class="">
        <div class="d-flex justify-content-between mb-2 flex-column flex-md-row">
          <h4>Reviews</h4>
          <div class="d-flex flex-column flex-md-row">
            <div style="width: 200px">
              <v-select v-model="review.customer.id"
                        @search="searchCustomers"
                        :options="customers"
                        :reduce="p => p.id"
                        label="displayName"></v-select>
            </div>
            <b-form-textarea v-model="review.comment" class="mx-md-2"></b-form-textarea>
            <b-button @click="saveReview()">
              <font-awesome-icon icon="save"></font-awesome-icon>
              Save Review
            </b-button>
          </div>
        </div>
        <table class="table">
          <thead>
          <tr>
            <th>Customer</th>
            <th>Review</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="r of reviews" :key="r.id">
            <td>{{ r.reviewer }}</td>
            <td>{{ r.comment }}</td>
          </tr>
          </tbody>
        </table>
      </b-tab>
    </b-tabs>
  </section>
</template>
<script>
import axios from 'axios'
import Rupee from '@/components/rupee'
import { NewReview } from '@/util'
import DisplayBrand from '@/components/display-brand'
import {formatDistanceToNow, subDays} from 'date-fns'

export default {
  name: 'SuperAdmin',
  components: {DisplayBrand, Rupee},
  data: () => ({
    mainProps: {blank: true, blankColor: '#777', width: 125, height: 125, class: 'm-1'},
    tab: 0,
    loadedTabs: {},
    orders: [],
    total: 0,
    page: 1,
    pageSize: 50,
    loadingOrders: false,
    loadingCarts: false,
    cartsLoaded: false,
    saving: {},
    addresses: {},
    orderStatuses: ['DRAFT', 'OPEN', 'SCHEDULED', 'WAITING_FOR_PICKUP', 'SHIPPED', 'DELIVERED', 'CANCELLED', 'CLOSED'],
    paymentStatuses: ['PAID', 'PENDING', 'LATER'],
    shipModes: ['SELF', 'WEFAST', 'SHIPROCKET', 'DELHIVERY', 'AMAZON', 'ECOM', 'FEDEX', 'XPRESSBEES', 'SHIPYAARI'],
    configs: [],
    carts: [],
    showingImg: null,
    attach: {},
    reviews: [],
    config: {
      theKey: '',
      theValue: ''
    },
    modeCfg: true,
    filterCfg: '',
    filterBrand: null,
    welcomeMessages: [],
    filter: '',
    orderStatus: 'ALL',
    paymentStatus: 'ALL',
    brandId: 'ALL',
    dnd: '',
    dnds: [],
    max: 7,
    review: NewReview()
  }),
  async created () {
    // e.g. /super-admin?status=CART&days=30 from the dashboard
    if (this.$route.query.status) this.orderStatus = this.$route.query.status
    if (+this.$route.query.days > 0) this.max = +this.$route.query.days
    await this.$store.dispatch('loadBrands')
    await this.loadTab(this.tab)
  },
  watch: {
    tab: 'loadTab'
  },
  computed: {
    filterConfigs () {
      console.log('filterConfigs', this.filterCfg, this.filterCfg === '', this.configs)
      let cfgs = this.configs
      if (this.filterCfg !== '')
        cfgs = cfgs.filter(c => c.theKey.theKey.toLowerCase().indexOf(this.filterCfg.toLowerCase()) > -1)
      if (this.filterBrand !== null)
        cfgs = cfgs.filter(c => c.theKey.brandId === this.filterBrand)

      return cfgs
    },
    isCartView () {
      return this.orderStatus === 'CART'
    },
    filteredCarts () {
      const f = this.filter.trim().toLowerCase()
      const since = subDays(new Date(), this.max)
      return this.carts
          .filter(c => this.brandId === 'ALL' || c.brandId === this.brandId)
          .filter(c => new Date(c.updatedAt || c.createdAt) > since)
          .filter(c => !f || c.customerName.toLowerCase().includes(f) || c.customerMobile.includes(f))
    },
    cartsTotal () {
      return this.filteredCarts.reduce((t, c) => t + c.total, 0)
    },
    customers () {
      return this.$store.state.searchCustomers
    },
    googleUrl () {
      return `${process.env.VUE_APP_URL}/oauth/login`
    },
  },
  methods: {
    makeUrl (a) {
      return process.env.VUE_APP_URL + '/public/attachment?file=' + a
    },
    showImg (a) {
      this.showingImg = process.env.VUE_APP_URL + '/public/attachment?file=' + a
      this.$bvModal.show('img')
    },
    async searchCustomers (search, loading) {
      if (search.length > 2) {
        loading(true)
        await this.$store.dispatch('searchCustomer', {search: search, area: this.area})
        loading(false)
      }
    },
    async addCFG () {
      this.configs.push({theKey: {}})
    },
    async resetCFG () {
      this.filterCfg = ''
      this.filterBrand = null
    },
    isSaved (c) {
      return Object.keys(c).indexOf('id') > -1
    },
    async saveCFG (cfg) {
      await axios.post('/api/super-admin/config', cfg)
      await this.loadCFG()
    },
    async deleteCFG (cfg) {
      await axios.delete(`/api/super-admin/config?id=${cfg.theKey.theKey}&brand_id=${cfg.theKey.brandId}`)
      await this.loadCFG()
    },
    // each tab loads its data the first time it is opened
    async loadTab (idx) {
      if (this.loadedTabs[idx]) return
      this.loadedTabs[idx] = true
      const loaders = [
        () => this.refresh(),
        () => this.loadWelcome(),
        () => this.loadDND(),
        () => this.loadCFG(),
        () => this.loadReviews(),
      ]
      if (loaders[idx]) await loaders[idx]()
    },
    refresh () {
      return this.isCartView ? this.loadCarts() : this.load()
    },
    ago (dt) {
      return dt ? formatDistanceToNow(new Date(dt), {addSuffix: true}) : ''
    },
    reload () {
      if (this.isCartView) {
        // carts are filtered in the browser, fetch them only once
        if (!this.cartsLoaded) this.loadCarts()
        return
      }
      this.page = 1
      this.load()
    },
    async load () {
      const params = {
        max: this.max,
        q: this.filter.trim(),
        page: this.page - 1,
        size: this.pageSize
      }
      if (this.orderStatus !== 'ALL') params.status = this.orderStatus
      if (this.paymentStatus !== 'ALL') params.pstatus = this.paymentStatus
      if (this.brandId !== 'ALL') params.brandId = this.brandId
      this.loadingOrders = true
      try {
        const {data} = await axios.get('/api/super-admin/orders', {params})
        this.orders = data.orders
        this.total = data.total
      } finally {
        this.loadingOrders = false
      }
    },
    addressOptions (o) {
      return this.addresses[o.customerId] ||
          [{id: o.customerAddressId, displayAddress: o.fullAddress || '-'}]
    },
    cartAddressOptions (c) {
      return this.addresses[c.customerId] || []
    },
    async loadAddresses (o) {
      if (!o.customerId || this.addresses[o.customerId]) return
      const {data} = await axios.get(`/api/admin/address?customer_id=${o.customerId}`)
      this.$set(this.addresses, o.customerId, data || [])
    },
    async loadWelcome () {

      let ps = await Promise.all(
          this.$store.state.brands.map(async (b) => {
            return await axios.get(`/api/super-admin/welcome?brandId=${b.id}`)
          })
      )
      this.welcomeMessages = ps.map(d => d.data).map(w => {
        w.attachmentsNew = []
        return w
      })

      console.log(this.welcomeMessages)
    },
    async loadDND () {
      this.dnds = (await axios.get(`/api/super-admin/dnd`)).data
    },
    async loadReviews () {
      this.reviews = (await axios.get(`/api/super-admin/review`)).data
    },
    async loadCarts () {
      this.loadingCarts = true
      try {
        const {data} = await axios.get('/api/super-admin/cart')
        this.carts = data.map(c => ({...c, addressId: null}))
        this.cartsLoaded = true
      } finally {
        this.loadingCarts = false
      }
    },
    async loadCFG () {
      this.configs = (await axios.get(`/api/super-admin/config`)).data
    },
    async updateOrder (o) {
      const fd = new FormData()
      fd.append('id', o.id)
      fd.append('address', o.customerAddressId || '')
      fd.append('status', o.orderStatus)
      fd.append('pstatus', o.paymentStatus)
      fd.append('smode', o.modeOfShipment)
      fd.append('sid', o.remoteShipId || '')
      fd.append('awb', o.trackingNumber || '')
      fd.append('url', o.trackingUrl || '')

      this.$set(this.saving, o.id, true)
      try {
        const {data} = await axios.put('/api/super-admin/orders', fd)
        const idx = this.orders.findIndex(x => x.id === o.id)
        if (idx > -1) this.orders.splice(idx, 1, data)
        this.$bvToast.toast(`Order #${o.orderNum} is Updated`, {
          title: 'Info',
          autoHideDelay: 3000,
          appendToast: true,
          variant: 'success'
        })
      } catch (e) {
        await this.load()
      } finally {
        this.$delete(this.saving, o.id)
      }
    },
    async saveWM (welcomeMessage) {
      let f = new FormData()
      f.append('message', welcomeMessage.message)
      f.append('sendCatalog', welcomeMessage.sendCatalog)
      f.append('brandId', welcomeMessage.brand.id)
      welcomeMessage.attachmentsNew.forEach(a => {
        f.append('files', a)
      })
      await axios.post(`/api/super-admin/welcome`, f)
      await this.loadWelcome()

      this.$bvToast.toast(`Welcome Message is Updated`, {
        title: 'Info',
        autoHideDelay: 5000,
        appendToast: true,
        variant: 'success'
      })
    },
    async saveDND () {
      await axios.post(`/api/super-admin/dnd`, {dnd: this.dnd})
      this.dnd = ''
      await this.loadDND()
    },
    async saveReview () {
      await axios.post(`/api/super-admin/review`, this.review)
      this.review = NewReview()
      await this.loadReviews()
    },
    async removeFromDND (d) {
      await axios.put(`/api/super-admin/dnd?id=${d.mobile}&dnd=false`)
      await this.loadDND()
    },
    async cartToOrder (c) {
      this.$set(this.saving, c.session, true)
      try {
        const {data} = await axios.post('/api/super-admin/cart', {session: c.session, addressId: c.addressId})
        this.carts = this.carts.filter(x => x !== c)
        this.$bvToast.toast(`Order #${data.orderNum} created for ${c.customerName}`, {
          title: 'Info', autoHideDelay: 5000, appendToast: true, variant: 'success'
        })
      } finally {
        this.$delete(this.saving, c.session)
      }
    },
    async deleteCart (c) {
      const ok = await this.$bvModal.msgBoxConfirm(`Delete the cart of ${c.customerName}?`, {
        okVariant: 'danger', okTitle: 'Delete', size: 'sm'
      })
      if (!ok) return
      await axios.delete(`/api/super-admin/cart?id=${encodeURIComponent(c.session)}`)
      this.carts = this.carts.filter(x => x !== c)
    },
  }
}
</script>
<style scoped>
.brand-display {
  padding: 0 !important;
}
</style>
